// src/pages/UsersList.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Collapse,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  ColumnDef,
} from '@tanstack/react-table';

import { getUsers } from '../../../services/ApiService';
import { GetUsersResponse } from '../../../models/GetUsersResponse';
import User from '../../../models/User';
import Spinner from '../../../components/Spinner/Spinner';
import { useSystemTables } from '../../../hooks/UseSystemTables';
import styles from './UsersList.module.css';
import EditUserDialog from '../../../components/EditUserDialog/EditUserDialog';

interface ColumnMeta {
  filterType?: 'select';
  filterOptions?: string[] | 'products' | 'roles' | 'apps';
}

const UsersList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data: usersData, isLoading: isLoadingUsers } = useQuery<GetUsersResponse>({
    queryKey: ['users'],
    queryFn: () => getUsers(),
  });

  const { data: systemTablesData, isLoading: isLoadingSystemTables } = useSystemTables();

  const users: User[] = usersData?.users || [];
  const clients = systemTablesData?.data.clients || [];
  const products = systemTablesData?.data.catalog || [];
  const userRoles = systemTablesData?.data.systemParameters?.userRole || [];
  const appTypes = systemTablesData?.data.systemParameters?.appType || [];

  const getClientName = (clientInnerId: string) => {
    const client = clients.find(c => c.clientInnerId === clientInnerId);
    return client ? client.clientName : clientInnerId;
  };

  const getProductName = (productId: string) => {
    const product = products.find(p => p.itemNumber === productId);
    return product ? product.itemNumber : '';
  };

  const getRoleName = (roleValue: string) => {
    if (!roleValue) return '';
    const role = userRoles.find(r => r.parameterValue === roleValue);
    if (!role) return roleValue;
    return i18n.language === 'he' ? role.hebName : role.engName;
  };

  const getAppName = (appValue: string) => {
    if (!appValue) return '';
    const app = appTypes.find(a => a.parameterValue === appValue);
    if (!app) return appValue;
    return i18n.language === 'he' ? app.hebName : app.engName;
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'custom:clientInnerId',
      header: t('User.ClientInnerId'),
      cell: ({ row }) => getClientName(row.original['custom:clientInnerId'] || ''),
      size: 120,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const clientInnerId = row.original['custom:clientInnerId'] || '';
        const clientName = getClientName(clientInnerId);
        return clientName.toLowerCase().includes(String(filterValue).toLowerCase());
      },
    },
    {
      accessorKey: 'username',
      header: t('User.UserName'),
      size: 100,
      cell: ({ row }) => (
        <Tooltip title={row.original.username} arrow>
          <Box sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.original.username}
          </Box>
        </Tooltip>
      ),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'email',
      header: t('User.Email'),
      size: 180,
      cell: ({ row }) => (
        <Tooltip title={row.original.email} arrow>
          <Box sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.original.email}
          </Box>
        </Tooltip>
      ),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'phone_number',
      header: t('User.PhoneNumber'),
      size: 100,
      cell: ({ row }) => (
        <Box sx={{ direction: 'ltr', textAlign: 'left' }}>
          {row.original.phone_number}
        </Box>
      ),
      filterFn: 'includesString',
    },
    {
      accessorKey: 'email_verified',
      header: t('User.EmailVerified'),
      size: 80,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original.email_verified || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: ['all', 'true', 'false'] },
    },
    {
      accessorKey: 'phone_number_verified',
      header: t('User.PhoneNumberVerified'),
      size: 80,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original.phone_number_verified || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: ['all', 'true', 'false'] },
    },
    {
      accessorKey: 'custom:role',
      header: t('User.Role'),
      size: 80,
      cell: ({ row }) => {
        const roleValue = (row.original['custom:role'] ?? '') as string;
        return getRoleName(roleValue);
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original['custom:role'] || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: 'roles' },
    },
    {
      accessorKey: 'custom:app',
      header: t('User.App'),
      size: 80,
      cell: ({ row }) => {
        const appValue = (row.original['custom:app'] ?? '') as string;
        return getAppName(appValue);
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original['custom:app'] || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: 'apps' },
    },
    {
      accessorKey: 'custom:assignedProduct',
      header: t('User.AssignedProduct'),
      size: 120,
      cell: ({ row }) => {
        const productId = row.original['custom:assignedProduct'];
        return productId ? getProductName(productId) : '';
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const productId = row.original['custom:assignedProduct'] || '';
        const productName = getProductName(productId);
        const searchValue = String(filterValue).toLowerCase();
        return productId.toLowerCase().includes(searchValue) || 
               productName.toLowerCase().includes(searchValue);
      },
    },
    {
      accessorKey: 'custom:deletionProtected',
      header: t('User.DeletionProtected'),
      size: 80,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original['custom:deletionProtected'] || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: ['all', 'true', 'false'] },
    },
    {
      accessorKey: 'custom:createUserWhatsapp',
      header: t('User.CreateUserWhatsapp'),
      size: 80,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original['custom:createUserWhatsapp'] || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: ['all', 'true', 'false'] },
    },
    {
      accessorKey: 'custom:orderingWhatsapp',
      header: t('User.OrderingWhatsapp'),
      size: 80,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original['custom:orderingWhatsapp'] || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: ['all', 'true', 'false'] },
    },
    {
      accessorKey: 'custom:siloWhatsapp',
      header: t('User.SiloWhatsapp'),
      size: 80,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue === 'all') return true;
        const value = row.original['custom:siloWhatsapp'] || '';
        return String(value).toLowerCase() === String(filterValue).toLowerCase();
      },
      meta: { filterType: 'select', filterOptions: ['all', 'true', 'false'] },
    },
    {
      accessorKey: 'groups',
      header: t('User.Groups'),
      size: 120,
      cell: ({ row }) => {
        const groups = row.original.groups;
        const groupNames = groups.map(g => g.GroupName).join(', ');
        return (
          <Tooltip title={groupNames} arrow>
            <Box sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {groupNames}
            </Box>
          </Tooltip>
        );
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const groups = row.original.groups || [];
        const groupNames = groups.map(g => g.GroupName).join(', ');
        const searchValue = String(filterValue).toLowerCase();
        return groupNames.toLowerCase().includes(searchValue);
      },
    },
  ];

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleFilterToggle = () => {
    setIsFilterOpen((prev) => !prev);
  };

  if (isLoadingUsers || isLoadingSystemTables) return <Spinner />;
  if (!usersData || !systemTablesData) return <div>Error loading data.</div>;

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('UsersList.Title')}</h2>
      <Box className={styles.generalSection}>
        <Box mb={2}>
          <Button
            variant="contained"
            onClick={handleFilterToggle}
          >
            {isFilterOpen ? t('Filters.Close') : t('Filters.Open')}
          </Button>
        </Box>

        <Collapse in={isFilterOpen}>
          <Box mb={2}>
            <Grid container spacing={2}>
              {table.getAllColumns().map((column) => {
                const meta = column.columnDef.meta as ColumnMeta | undefined;
                const filterType = meta?.filterType;
                const filterOptions = meta?.filterOptions;

                if (filterType === 'select') {
                  let options: { value: string; label: string }[] = [];

                  if (filterOptions === 'products') {
                    options = [
                      { value: 'all', label: t('General.Labels.All') || 'All' },
                      ...products.map((product) => ({
                        value: product.itemNumber,
                        label: product.itemNumber,
                      })),
                    ];
                  } else if (filterOptions === 'roles') {
                    options = [
                      { value: 'all', label: t('General.Labels.All') || 'All' },
                      ...userRoles.map((role) => ({
                        value: role.parameterValue,
                        label: i18n.language === 'he' ? role.hebName : role.engName,
                      })),
                    ];
                  } else if (filterOptions === 'apps') {
                    options = [
                      { value: 'all', label: t('General.Labels.All') || 'All' },
                      ...appTypes.map((app) => ({
                        value: app.parameterValue,
                        label: i18n.language === 'he' ? app.hebName : app.engName,
                      })),
                    ];
                  } else if (Array.isArray(filterOptions)) {
                    const useTrueFalse = column.id === 'email_verified' || column.id === 'custom:deletionProtected';
                    options = filterOptions.map((option) => {
                      if (option === 'all') {
                        return { value: 'all', label: t('General.Labels.All') || 'All' };
                      } else if (option === 'true') {
                        return { 
                          value: 'true', 
                          label: useTrueFalse 
                            ? (t('General.Labels.True') || 'True')
                            : (t('General.Labels.Yes') || 'Yes')
                        };
                      } else if (option === 'false') {
                        return { 
                          value: 'false', 
                          label: useTrueFalse
                            ? (t('General.Labels.False') || 'False')
                            : (t('General.Labels.No') || 'No')
                        };
                      }
                      return { value: option, label: option };
                    });
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} key={column.id}>
                      <FormControl fullWidth>
                        <InputLabel>{column.columnDef.header as string}</InputLabel>
                        <Select
                          value={(column.getFilterValue() as string) || 'all'}
                          label={column.columnDef.header as string}
                          onChange={(event) =>
                            column.setFilterValue(event.target.value)
                          }
                        >
                          {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={column.id}>
                    <TextField
                      fullWidth
                      label={column.columnDef.header as string}
                      value={(column.getFilterValue() as string) ?? ''}
                      onChange={(event) =>
                        column.setFilterValue(event.target.value)
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Collapse>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell key={header.id}>
                      <TableSortLabel
                        active={header.column.getIsSorted() !== false}
                        direction={header.column.getIsSorted() === 'asc' ? 'asc' : 'desc'}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow 
                  key={row.id}
                  onClick={() => setSelectedUser(row.original)}
                  sx={{ cursor: 'pointer' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={styles.paginationContainer}>
          <Box className={styles.rowsPerPage}>
            {t('Pagination.Labels.RowsPerPage')}:
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </Box>
          <Box className={styles.paginationControls}>
            <Button
              variant="contained"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              sx={{ minWidth: '100px' }}
            >
              {t('Pagination.Labels.PreviousPage')}
            </Button>
            <Box sx={{ minWidth: '150px', textAlign: 'center' }}>
              {t('Pagination.Labels.CurrentPage', { currentPage: table.getState().pagination.pageIndex + 1, totalPages: table.getPageCount() })}
            </Box>
            <Button
              variant="contained"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              sx={{ minWidth: '100px' }}
            >
              {t('Pagination.Labels.NextPage')}
            </Button>
          </Box>
        </Box>
      </Box>

      {selectedUser && (
        <EditUserDialog
          user={selectedUser}
          open={!!selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default UsersList;